import { CoreMenuItem, CoreSelect, CoreSelectProps } from "@verity/components/core";
import type { SessionInfo } from "../../../TestResultsWrapper";
import i18n from "../../../../../i18n";
import { Box, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SessionStatusChip from "./StatusChip";

type AttemptSelectorProps = {
	sessionNumber: number;
	onChange: (value: number) => void;
	proctoringSessions: SessionInfo[];
} & Omit<CoreSelectProps<number>, "value" | "onChange" | "children">;

export default function AttemptSelector({
	sessionNumber,
	onChange,
	proctoringSessions,
	...props
}: AttemptSelectorProps) {
	const totalSessions = proctoringSessions.length;
	return (
		<CoreSelect<number>
			sx={{
				maxWidth: "100%",
				width: "100%",
				height: "35px",
				fontSize: "15px",
				fontWeight: "400",
				padding: 0,
				display: "flex",
				alignItems: "center",
				...(props.sx || {}),
			}}
			value={sessionNumber - 1} // sessionNumber is 1-indexed, but the select value should be 0-indexed
			onChange={(event) => {
				const index = Number(event.target.value);
				onChange(index);
			}}
			label={i18n.t("result.components.resultsPage.ariaLabels.attemptComboBox")}
			renderValue={() =>
				i18n.t("result.components.resultsPage.proctoringResultsViewTSX.attempt", {
					current: sessionNumber,
					total: totalSessions,
				})
			}
			MenuProps={{
				MenuListProps: {
					onKeyDown: (event) => {
						if (event.key === "Tab") {
							event.stopPropagation(); // Allows tab navigation
						}
					},
					...(props.MenuProps?.MenuListProps || {}),
				},
				...props.MenuProps,
			}}
			{...props}
		>
			{proctoringSessions.map((_, index) => {
				const isSyncedToLms = Boolean(proctoringSessions[index].syncedToLmsAt);
				const row = (
					<Box display="flex" justifyContent="space-between" alignItems="center" width="100%" gap={1}>
						<Box display="flex" alignItems="center" gap={0.5}>
							{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.attempt2", {
								attempt: index + 1,
								total: totalSessions,
							})}
							{isSyncedToLms && <CheckCircleIcon color="success" fontSize="small" />}
						</Box>
						<SessionStatusChip status={proctoringSessions[index].status} />
					</Box>
				);
				return (
					<CoreMenuItem key={index} value={index}>
						{isSyncedToLms ? (
							<Tooltip
								title={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.syncedToLmsTooltip")}
								placement="right"
								arrow
							>
								{row}
							</Tooltip>
						) : (
							row
						)}
					</CoreMenuItem>
				);
			})}
		</CoreSelect>
	);
}
